import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, User, Tag, AlertCircle, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import TaskDetailSheet from '../components/TaskDetailSheet';

const COLUMNS = [
  { key: 'locked', label: 'Locked', bg: '#BDC3C7', text: '#FFFFFF', border: '#95a5a6' },
  { key: 'to_be_done', label: 'To Be Done', bg: '#E2E8F0', text: '#34495e', border: '#CBD5E1' },
  { key: 'doing', label: 'Doing', bg: '#f39c12', text: '#FFFFFF', border: '#d68910' },
  { key: 'need_clarification', label: 'Need Clarification', bg: '#e74c3c', text: '#FFFFFF', border: '#c0392b' },
  { key: 'done', label: 'Done', bg: '#27ae60', text: '#FFFFFF', border: '#2ecc71' },
];

const isOverdue = (task) => {
  if (task.status === 'done' || !task.estimated_completion) return false;
  return new Date() > new Date(task.estimated_completion);
};

export default function KanbanPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, catsRes] = await Promise.all([
        api.get('/tasks'), api.get('/users'), api.get('/categories'),
      ]);
      setTasks(tasksRes.data.tasks);
      setUsers(usersRes.data.users);
      setCategories(catsRes.data.categories);
    } catch (err) { console.error('Failed to fetch:', err); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task updated');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const getUserName = (task) => {
    if (task?.assigned_to_removed_name) return task.assigned_to_removed_name;
    if (!task?.assigned_to) return 'Unassigned';
    return users.find(u => u.user_id === task.assigned_to)?.name || 'Unassigned';
  };
  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.name || '';
  const getCategoryColor = (catId) => categories.find(c => c.category_id === catId)?.color || '#7F8C8D';
  const canModifyTask = (task) => user?.role === 'admin' || task.assigned_to === user?.user_id;

  const filteredTasks = tasks.filter(t => {
    if (filterCategory !== 'all' && t.category_id !== filterCategory) return false;
    return true;
  });

  return (
    <div data-testid="kanban-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e]">Team Task Board</h2>
          <p className="text-sm text-[#7F8C8D]">{tasks.length} tasks across {COLUMNS.length} columns</p>
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px] rounded-sm border-[#E2E8F0]" data-testid="kanban-filter-category">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="kanban-board">
        {COLUMNS.map(col => {
          const columnTasks = filteredTasks.filter(t => t.status === col.key);
          return (
            <div key={col.key} className="flex flex-col min-h-[300px]" data-testid={`kanban-col-${col.key}`}>
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: col.bg }} />
                  <h3 className="text-sm font-bold text-[#34495e]">{col.label}</h3>
                </div>
                <span
                  className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-sm text-[11px] font-bold"
                  style={{ backgroundColor: col.bg, color: col.text }}
                  data-testid={`kanban-count-${col.key}`}
                >
                  {columnTasks.length}
                </span>
              </div>

              {/* Column Body */}
              <div className="flex-1 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-240px)]">
                {columnTasks.length === 0 ? (
                  <div className="flex items-center justify-center h-24 text-xs text-[#BDC3C7]" data-testid={`kanban-empty-${col.key}`}>
                    No tasks
                  </div>
                ) : (
                  columnTasks.map(task => {
                    const overdue = isOverdue(task);
                    const catName = getCategoryName(task.category_id);
                    const catColor = getCategoryColor(task.category_id);

                    return (
                      <div
                        key={task.task_id}
                        className={`bg-white border rounded-sm p-3 cursor-pointer transition-all hover:scale-[1.01] hover:shadow-sm ${
                          task.status === 'locked' ? 'border-[#BDC3C7] opacity-60' : overdue ? 'border-[#e74c3c] bg-red-50' : 'border-[#E2E8F0]'
                        }`}
                        onClick={() => setSelectedTaskId(task.task_id)}
                        data-testid={`kanban-card-${task.task_id}`}
                      >
                        {/* Title */}
                        <p className={`text-sm font-normal leading-snug mb-1.5 ${task.status === 'locked' ? 'text-[#BDC3C7]' : overdue ? 'text-[#e74c3c] font-bold' : 'text-[#34495e]'}`}>
                          {task.status === 'locked' && <Lock size={11} className="inline mr-1 -mt-0.5 text-[#BDC3C7]" />}
                          {overdue && <AlertCircle size={12} className="inline mr-1 -mt-0.5 text-[#e74c3c]" />}
                          {task.title}
                        </p>
                        {task.status === 'locked' && (
                          <p className="text-[10px] text-[#BDC3C7] italic mb-1.5">Waiting for previous task</p>
                        )}

                        {/* Description preview */}
                        {task.description && (
                          <p className="text-[11px] text-[#BDC3C7] mb-2 line-clamp-2 leading-relaxed">{task.description}</p>
                        )}

                        {/* Meta row */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[#7F8C8D]">
                          {catName && (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: catColor }} />
                              {catName}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <User size={10} />
                            <span className={task.assigned_to_removed_name ? 'text-[#e74c3c] italic' : ''}>{getUserName(task)}</span>
                          </span>
                          {task.estimated_completion && (
                            <span className={`flex items-center gap-1 ${overdue ? 'text-[#e74c3c] font-bold' : ''}`}>
                              <Clock size={10} />
                              {format(new Date(task.estimated_completion), 'MMM d')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTaskId(null)}
        users={users}
        categories={categories}
        onTaskUpdated={() => fetchData()}
      />
    </div>
  );
}
